/**
 * Shown to a signed-in member who hasn't been granted any events yet.
 * Lets them send a one-click access request to the admin (POST /request-access),
 * which surfaces in the admin console's "Pending requests" section.
 */
import { useState } from 'react';
import { IconLock, IconCheck } from '@tabler/icons-react';
import { EmptyState } from '../ui/EmptyState.tsx';
import { Button } from '../ui/Button.tsx';
import { Spinner } from '../ui/Spinner.tsx';
import { requestAccess } from '../../lib/apiClient.ts';
import { toast } from '../../state/toastStore.ts';

export function NoAccess({ email, onSignOut }: { email: string; onSignOut: () => void }) {
  const [busy, setBusy] = useState(false);
  const [requested, setRequested] = useState(false);

  const request = async () => {
    setBusy(true);
    try {
      await requestAccess();
      setRequested(true);
      toast.success('Access request sent to the admin');
    } catch {
      toast.error('Could not send request. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <EmptyState
      icon={requested ? IconCheck : IconLock}
      title={requested ? 'Request sent' : 'No access yet'}
      description={
        requested
          ? `Thanks! The gallery admin has been notified and will grant ${email} access to the relevant events.`
          : `You're signed in as ${email}, but haven't been given access to any events yet. Request access and the gallery admin will review it.`
      }
      action={
        <div className="flex flex-col items-center gap-3">
          {!requested && (
            <Button variant="primary" onClick={request} disabled={busy}>
              {busy ? <Spinner className="h-4 w-4" /> : <IconLock className="h-4 w-4" />}
              Request access
            </Button>
          )}
          <Button variant="secondary" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      }
    />
  );
}
